/**
 * Pure translation helpers shared by the masked input components.
 * They hold no React state and touch no DOM, so they are unit-testable on their own;
 * the interactive behaviour they feed into is covered by the Playwright suite in a real browser,
 * where caret and selection semantics are genuine rather than emulated.
 */

/**
 * Character marking an editable digit slot in the project mask syntax, e.g. `'99 999 99'`.
 */
export const MASK_DIGIT_CHAR = '9';

/**
 * Translates the project mask syntax into the `@react-input/mask` syntax,
 * where the placeholder character itself marks an editable slot.
 * `'99 999 99'` with placeholder `'X'` becomes `'XX XXX XX'` with `{ X: /\d/ }`.
 * The placeholder must not be a digit — `@react-input/mask` ignores replacement keys while typing,
 * so a digit placeholder would make that digit impossible to enter.
 */
export const toMaskPattern = (mask: string, maskPlaceholder: string): string =>
  mask.split(MASK_DIGIT_CHAR).join(maskPlaceholder);

/**
 * Appends the untouched tail of the mask so unfilled slots stay visible,
 * matching the value `showMask` renders while the user types.
 */
export const padWithMask = (formatted: string, maskPattern: string): string =>
  formatted + maskPattern.slice(formatted.length);

/**
 * Removes the placeholder tail added by {@link padWithMask},
 * cutting the value after the last position the user actually filled,
 * so no placeholder character is ever handed to a consumer.
 *
 * Any mask literal that precedes that position is retained, because it is part of the masked value:
 * the phone mask yields `'+7 (912'` for three entered digits, not `'912'`.
 * A consumer therefore still has to reduce the result to the data it wants to store —
 * `RussianPhoneNumberField` runs it through `toRussianNationalDigits`.
 *
 * The scan runs forward and keeps the last differing index rather than stopping at the first mismatch from the right,
 * so a filled position is found even if some later one coincides with the pattern.
 */
export const stripMaskTail = (value: string, maskPattern: string): string => {
  let lastFilled: number = -1;
  for (let index: number = 0; index < value.length; index++) {
    if (value[index] !== maskPattern[index]) {
      lastFilled = index;
    }
  }
  return value.slice(0, lastFilled + 1);
};
